import { useEffect, useId, useMemo, useState, type ReactNode } from "react";
import { ArrowDown, ArrowUp } from "lucide-react";
import { exportVisibleColumns } from "./tableExcel";
import { nextMobileSort, sortMobileRows, type MobileSortState } from "./mobileSorting";
import { storeOverviewComparisonLabel, storeOverviewCopy, storeTargetRemainder, type StoreOverviewColumn } from "./storeOverviewPresentation";
import { desktopMetricHelp } from "./desktopInformation";
import { TableWindowSpacer, useTableWindow } from "./tableWindow";
import { executiveOverviewLayout, type ExistingExecutiveColumn } from "./executiveOverviewPresentation";
import "./storeOverview.css";
import "./executiveOverview.css";
const integer = new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 0 });
const percent = new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

type DisplayRow = Record<string, string | number | null>;
type Props = {
  rows: DisplayRow[];
  totalRow: DisplayRow;
  columns: ExistingExecutiveColumn[];
  language: string;
  hiddenColumns: Set<string>;
  expectedTarget: number | null;
  expectedPerRow: boolean;
  singleMonthLabel?: string;
  remainderSources?: { label: string; target_uzs: number | null; net_sales_uzs: number }[];
  remainderTotal?: { target: number; sales: number };
  targetProgress?: { asOfDate: string; fullMonthTarget: boolean };
  ariaLabel: string;
  currentSort: () => MobileSortState;
  parseNumber: (value: unknown) => number | null;
  onViewChange: (sort: MobileSortState, rowIds: string[]) => void;
  children: ReactNode;
};

const mobileQuery = "(max-width: 900px), (hover: none) and (pointer: coarse) and (orientation: landscape) and (max-height: 600px)";

// Keep the established mobile summary/detail explorer instead of mounting two
// competing tables/export registries. The new desktop view uses supplied values.
export default function ExecutiveOverviewTable(props: Props) {
  const [mobile, setMobile] = useState(() => window.matchMedia(mobileQuery).matches);
  useEffect(() => {
    const query = window.matchMedia(mobileQuery);
    const update = () => setMobile(query.matches);
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  return mobile ? props.children : <DesktopExecutiveOverview {...props} />;
}

function DesktopExecutiveOverview(props: Props) {
  const singleMonth = Boolean(props.singleMonthLabel);
  const columns = executiveOverviewLayout(props.columns, props.language, props.hiddenColumns, Boolean(props.remainderSources));
  const [sort, setSort] = useState(props.currentSort);
  const referenceId = useId() + "-expected";
  const c = storeOverviewCopy(props.language);
  const columnSignature = JSON.stringify(props.columns.filter(column => !props.hiddenColumns.has(column.key)).map(column => column.key));
  useEffect(() => {
    exportVisibleColumns.set(props.ariaLabel, JSON.parse(columnSignature));
    return () => { exportVisibleColumns.delete(props.ariaLabel); };
  }, [props.ariaLabel, columnSignature]);
  const remainderSources = new Map(props.remainderSources?.map(row => [row.label, row]));
  const remainder = (row: DisplayRow, total = false) => {
    const source = remainderSources.get(String(row.__row_id ?? row.label));
    return storeTargetRemainder(total ? props.remainderTotal?.target ?? null : source?.target_uzs ?? null,
      total ? props.remainderTotal?.sales ?? 0 : source?.net_sales_uzs ?? 0,
      props.targetProgress?.asOfDate, props.targetProgress?.fullMonthTarget ?? true);
  };
  const sorted = useMemo(() => sortMobileRows(props.rows, sort, (row, key) => key === "remaining" ? remainder(row).remaining : key === "label" || key === "weekday"
    ? String(row["__sort_" + key] ?? row[key] ?? "")
    : props.parseNumber(row["__sort_" + key] ?? row[key])), [props.rows, sort, props.parseNumber, props.remainderSources, props.remainderTotal, props.targetProgress]);
  const rowSignature = useMemo(() => JSON.stringify(sorted.map(row => row.__row_id ?? row.label)), [sorted]);
  const windowed = useTableWindow(sorted.length, rowSignature + JSON.stringify(sort));
  useEffect(() => {
    props.onViewChange(sort, sorted.map(row => String(row.__row_id ?? row.label)));
  }, [rowSignature, sort, props.onViewChange]);
  const width = columns.reduce((sum, column) => sum + column.width, 0);
  const groupEnd = (index: number) => index < columns.length - 1 && columns[index + 1].group !== columns[index].group;
  const tone = (row: DisplayRow, key: string) => {
    if (key === "targetRate") {
      const actual = props.parseNumber(row.__sort_targetRate ?? row.targetRate);
      const expected = props.parseNumber(row.__expected_targetRate);
      return actual == null ? "" : actual >= (expected ?? 100) ? "is-positive" : "is-negative";
    }
    if (key.endsWith("Change")) {
      const value = String(row[key] ?? "");
      return value.startsWith("+") ? "is-positive" : value.startsWith("-") ? "is-negative" : "";
    }
    return "";
  };
  const expected = (row: DisplayRow) => props.parseNumber(row.__expected_targetRate);
  const sortButton = (column: StoreOverviewColumn, comparison = false) => <button type="button" data-sort-column={column.key}
    onClick={() => setSort(nextMobileSort(sort, column.key))} aria-label={column.label + " · " + c[25]} title={desktopMetricHelp(column.key, props.language)}>
    {sort?.key === column.key ? sort.direction === "asc" ? <ArrowUp size={12} aria-hidden /> : <ArrowDown size={12} aria-hidden /> : null}
    <span>{comparison ? storeOverviewComparisonLabel(props.language) : column.label}{column.key === "remaining" ? <><br />{c[5]}</> : null}</span>
  </button>;
  const renderRow = (row: DisplayRow, total: boolean, rowIndex?: number) => <tr aria-rowindex={rowIndex} key={total ? "__total" : String(row.__row_id ?? row.label)} className={total ? "store-overview-total" : undefined}>
    {columns.map((column, index) => <td key={column.key} data-column={column.key}
      className={[groupEnd(index) ? "store-overview-boundary" : "", column.key === "label" ? "store-overview-label" : "", tone(row, column.key)].filter(Boolean).join(" ")}>
      {column.key === "remaining" ? <span className="store-overview-remainder">
        <strong>{remainder(row, total).remaining == null ? "—" : integer.format(remainder(row, total).remaining!)}</strong>
        <span title={c[5]}>{remainder(row, total).daily == null ? "—" : integer.format(Math.round(remainder(row, total).daily!))}</span>
      </span> : column.comparison ? <span className="store-overview-ly-pair">
        <span data-metric={column.key}>{String(row[column.key] ?? "—")}</span>
        <span data-metric={column.comparison.key} className={tone(row, column.comparison.key)} title={column.comparison.label}>{String(row[column.comparison.key] ?? "—")}</span>
      </span> : column.key === "targetRate" && props.expectedPerRow && expected(row) != null ? <span className="store-overview-ly-pair executive-overview-target-pair">
        <span data-metric="targetRate">{String(row.targetRate ?? "—")}</span>
        <span data-metric="expectedTargetRate" title={c[18]}>{"%" + percent.format(expected(row)!)}</span>
      </span> : column.key === "label" && total && singleMonth ? `${String(row[column.key] ?? "—")} · ${props.singleMonthLabel}` : String(row[column.key] ?? "—")}
    </td>)}
  </tr>;
  return <div className="store-overview-view is-fit executive-overview-table">
    <div className="store-overview-scroll" role="region" aria-label={props.ariaLabel} tabIndex={0} ref={windowed.scrollRef} onScroll={windowed.onScroll}>
      <table className="store-overview-table" style={{ minWidth: width }} aria-rowcount={singleMonth ? 2 : sorted.length + 2}>
        <caption className="store-overview-sr-only">{props.ariaLabel}</caption>
        <colgroup>{columns.map(column => <col key={column.key} style={{ width: column.width / width * 100 + "%" }} />)}</colgroup>
        <thead><tr className="store-overview-headings">{columns.map((column, index) => {
          const reference = column.key === "targetRate" && props.expectedTarget != null;
          return <th key={column.key} scope="col" data-column={column.key} data-comparison-column={column.comparison?.key}
            aria-sort={sort && (sort.key === column.key || sort.key === column.comparison?.key) ? sort.direction === "asc" ? "ascending" : "descending" : "none"}
            aria-describedby={reference ? referenceId : undefined}
            className={[groupEnd(index) ? "store-overview-boundary" : "", column.key === "label" ? "store-overview-label" : "", column.comparison ? "store-overview-paired-heading" : "", reference ? "store-overview-reference-heading" : "", column.comparison || reference || column.key === "remaining" ? "store-overview-two-line-heading" : ""].filter(Boolean).join(" ")}>
            {sortButton(column)}
            {reference ? <span id={referenceId} className="store-overview-target-reference" role="note">{c[18]}{props.expectedPerRow ? " %" : ": %" + percent.format(props.expectedTarget!)}</span> : null}
            {column.comparison ? sortButton(column.comparison, true) : null}
          </th>;
        })}</tr></thead>
        <tbody>{renderRow(props.totalRow, true, 2)}{!singleMonth ? <><TableWindowSpacer height={windowed.before} columns={columns.length} />
          {sorted.slice(windowed.start, windowed.end).map((row, index) => renderRow(row, false, windowed.start + index + 3))}
          <TableWindowSpacer height={windowed.after} columns={columns.length} /></> : null}</tbody>
      </table>
    </div>
  </div>;
}
